import axios from 'axios';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { apiSlice } from '../../store/api/apiSlice';
import { ENV_CONFIG } from '../../shared/config/env.config';
import type { MenuItem, MenuOptionsResponse } from './Navbar.model';

// The CloudFront JSON is a flat array; the legacy API returned a keyed object.
// Support both so consumers can branch on the shape.
type MenuOptionsResult = MenuItem[] | MenuOptionsResponse;

export const menuApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // The app menu is now served as a static nested JSON on CloudFront (same
    // output the API returned). ENV_CONFIG.appMenuUrl resolves to the staging
    // file for LOCAL/STAGING and the prod file for PRODUCTION. We use queryFn so
    // the request hits the absolute CloudFront URL directly, bypassing the
    // service base-URL map in dynamicBaseQuery. Only the data source changed —
    // the response shape and caching behavior are unchanged.
    getMenuOptions: builder.query<MenuOptionsResult, void>({
      queryFn: async () => {
        try {
          const { data } = await axios.get<MenuOptionsResult>(
            ENV_CONFIG.appMenuUrl
          );
          // cache to localStorage on fetch (same as before)
          localStorage.setItem('menuOptions', JSON.stringify(data));
          return { data };
        } catch (error: unknown) {
          const err = error as {
            response?: { status?: number };
            message?: string;
          };
          return {
            error: {
              status: err.response?.status ?? 'FETCH_ERROR',
              data: err.message ?? 'Failed to load app menu',
            } as FetchBaseQueryError,
          };
        }
      },
      // keepUnusedDataFor: 86400, // keep in RTK cache for 24hrs
    }),
  }),
});

export const { useGetMenuOptionsQuery, useLazyGetMenuOptionsQuery } = menuApi;
